/**
 * File compression utilities for images and videos
 */

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB in bytes

/**
 * Check if file size exceeds the maximum allowed size
 */
export const checkFileSize = (file: File): { valid: boolean; message?: string } => {
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      message: `File size (${formatFileSize(file.size)}) exceeds the maximum allowed size of 50MB. Please choose a smaller file.`
    }
  }
  return { valid: true }
}

/**
 * Format file size to human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Compress image file using Canvas API
 * @param file - Image file to compress
 * @param maxWidth - Maximum width (default: 1920)
 * @param maxHeight - Maximum height (default: 1080)
 * @param quality - Compression quality 0-1 (default: 0.8)
 * @returns Compressed File or original file if compression fails
 */
export const compressImage = async (
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1080,
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve) => {
    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      resolve(file) // Return original if not an image
      return
    }

    const reader = new FileReader()
    
    reader.onload = (e) => {
      const img = new Image()
      
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width
        let height = img.height

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = width * ratio
          height = height * ratio
        }

        // Create canvas and compress
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          resolve(file) // Return original if canvas context fails
          return
        }

        ctx.drawImage(img, 0, 0, width, height)

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new File from blob
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              })
              resolve(compressedFile)
            } else {
              resolve(file) // Return original if blob creation fails
            }
          },
          file.type,
          quality
        )
      }

      img.onerror = () => {
        resolve(file) // Return original if image load fails
      }

      if (e.target?.result) {
        img.src = e.target.result as string
      } else {
        resolve(file)
      }
    }

    reader.onerror = () => {
      resolve(file) // Return original if read fails
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Compress video file (basic validation - actual compression would require FFmpeg.wasm)
 * For now, we'll just validate size and return the file
 * Note: Full video compression in browser requires FFmpeg.wasm which is heavy
 * @param file - Video file
 * @returns Original file (compression not implemented, just validation)
 */
export const compressVideo = async (file: File): Promise<File> => {
  // Video compression in browser is complex and requires FFmpeg.wasm
  // For now, we'll just validate the size
  // The backend should handle video compression if needed
  return file
}

/**
 * Validate and compress file based on type
 */
export const processFile = async (file: File): Promise<{ file: File; error?: string }> => {
  // First check file size
  const sizeCheck = checkFileSize(file)
  if (!sizeCheck.valid) {
    return { file, error: sizeCheck.message }
  }

  try {
    let processedFile = file

    // Compress images
    if (file.type.startsWith('image/')) {
      processedFile = await compressImage(file)
    }
    // For videos, we just validate size (compression would require FFmpeg.wasm)
    else if (file.type.startsWith('video/')) {
      processedFile = await compressVideo(file)
    }

    // Check compressed size
    const compressedSizeCheck = checkFileSize(processedFile)
    if (!compressedSizeCheck.valid) {
      return { file: processedFile, error: compressedSizeCheck.message }
    }

    return { file: processedFile }
  } catch (error) {
    console.error('Error processing file:', error)
    return { file, error: 'Failed to process file. Please try again.' }
  }
}

